import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./landing.css";
import { FiPayLogo } from "./components/FiPayLogo";
import { InquiryForm } from "./components/InquiryForm";
import { OFFICIAL_LINKS } from "./data/servicesData";
import {
  ShieldCheck, Zap, Percent, Headphones, CheckCircle2, Phone, MessageCircle, Star, Clock, CheckCircle,
} from "lucide-react";

// Ad landing page: /apply?service=<key>&utm_source=...  (thank-you state at /apply/thank-you)

const TRACKING_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];
const TRACKING_STORE = "fpb_ad_tracking";

const DEFAULT_OFFER = {
  solution: "Payment Gateway (Pay-In)",
  eyebrow: "Pay-In · Payout · Loans · Insurance",
  title: "Grow your business with India's trusted fintech partner",
  subtitle: "Payment gateways, instant payouts, business loans and insurance — set up by one dedicated Hyderabad team.",
  points: [
    "Onboarding with PhonePe, Paytm, Razorpay, Cashfree & Subpaisa",
    "Instant T+0 / T+1 settlements across all major Indian banks",
    "Lowest MDR slabs and transparent pricing",
    "Dedicated relationship manager on call & WhatsApp",
  ],
};

const OFFERS = {
  "payment-gateway": {
    solution: "Payment Gateway (Pay-In)",
    eyebrow: "Payment Gateway",
    title: "Accept UPI, cards & net banking — live in 24 hours",
    subtitle: "Get onboarded on top Indian payment gateways with the lowest MDR and instant settlements.",
    points: [
      "PhonePe, Paytm, Razorpay, Cashfree, Unlimit & Subpaisa",
      "UPI intent, dynamic QR, cards & 50+ net banking banks",
      "T+0 instant settlement to your bank account",
      "Express KYC with a dedicated onboarding manager",
    ],
  },
  payouts: {
    solution: "Instant Payouts & Disbursals (T+0 / T+1)",
    eyebrow: "Instant Payouts",
    title: "Send money to any bank account in seconds, 24×7",
    subtitle: "Automate vendor, salary, refund and customer payouts via IMPS, NEFT, RTGS and UPI.",
    points: [
      "All major Indian banks supported",
      "Bulk CSV uploads and REST payout API",
      "Penny-drop name validation before every transfer",
      "Maker-checker approvals and full audit trail",
    ],
  },
  loans: {
    solution: "Business & MSME Loans",
    eyebrow: "Business & Personal Loans",
    title: "Get the right loan from 15+ banks & NBFCs",
    subtitle: "Collateral-free business loans up to ₹2 Crores and personal loans up to ₹40 Lakhs.",
    points: [
      "Compare offers from multiple lenders in one application",
      "Rates starting from 10.49% p.a.",
      "Sanction in as little as 48 hours",
      "Transparent fees, no hidden charges",
    ],
  },
  "personal-loan": {
    solution: "Personal Loans",
    eyebrow: "Personal Loans",
    title: "Personal loans up to ₹40 Lakhs with minimal paperwork",
    subtitle: "Quick approvals for salaried and self-employed professionals from leading banks.",
    points: [
      "Loans from ₹50,000 to ₹40 Lakhs",
      "Rates starting from 10.49% p.a.",
      "Money in your account within 24 hours of documents",
      "Expert help choosing the best lender",
    ],
  },
  "home-loan": {
    solution: "Home Loans & Loan Against Property (LAP)",
    eyebrow: "Home Loans & LAP",
    title: "Home loans & loan against property from 8.35% p.a.",
    subtitle: "Compare offers from SBI, HDFC, ICICI, LIC Housing and more with doorstep support.",
    points: [
      "Loans from ₹10 Lakhs up to ₹25 Crores",
      "Tenures up to 30 years",
      "Doorstep document collection",
      "Legal and valuation support included",
    ],
  },
  insurance: {
    solution: "Health & Mediclaim Insurance",
    eyebrow: "Insurance",
    title: "Health, life & motor insurance from India's top insurers",
    subtitle: "Compare IRDA-licensed plans and get expert help choosing the right cover.",
    points: [
      "Star Health, HDFC ERGO, ICICI Lombard, Tata AIA & more",
      "Cashless treatment at 12,000+ hospitals",
      "Instant motor policy in under 2 minutes",
      "Claim assistance from our team",
    ],
  },
  pos: {
    solution: "Smart Android POS Machines",
    eyebrow: "POS & Soundbox",
    title: "Smart POS machines & soundboxes for your shop",
    subtitle: "Accept cards, tap-to-pay and UPI QR with instant voice alerts in your language.",
    points: [
      "PhonePe, Paytm, BharatPe & Pine Labs devices",
      "Voice alerts in Telugu, Hindi, English & more",
      "Next-day or same-day settlement",
      "Doorstep installation and training",
    ],
  },
  api: {
    solution: "Fintech APIs (Payout, AEPS, MATM, DMT, CMS, Recharge, Travel)",
    eyebrow: "Fintech & Verification APIs",
    title: "Launch your fintech portal with 14+ ready APIs",
    subtitle: "Payout, AEPS, MATM, DMT, BBPS, travel and full KYC verification under one API key.",
    points: [
      "PAN, Aadhaar, bank penny-drop, UPI & GSTIN verification",
      "AEPS, MATM, DMT, CMS, recharge & BBPS",
      "Sandbox, docs & Postman collections",
      "Technical integration support",
    ],
  },
  partner: {
    solution: "Become a Partner (Refer & Earn)",
    eyebrow: "Partner Program",
    title: "Earn up to 2.5% on loans and up to 40% on insurance",
    subtitle: "Refer customers needing loans or insurance and get paid directly to your bank account.",
    points: [
      "Free to join, no investment",
      "Commissions paid by instant bank transfer",
      "Track referrals in your partner dashboard",
      "Training and support from our team",
    ],
  },
};
OFFERS["pay-in"] = OFFERS["payment-gateway"];
OFFERS.payout = OFFERS.payouts;
OFFERS["business-loan"] = OFFERS.loans;
OFFERS.validation = OFFERS.api;

const readTracking = (search) => {
  const params = new URLSearchParams(search);
  let stored = {};
  try {
    stored = JSON.parse(sessionStorage.getItem(TRACKING_STORE) || "{}");
  } catch {
    stored = {};
  }
  const fresh = {};
  TRACKING_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) fresh[k] = v.slice(0, 200);
  });
  const tracking = Object.keys(fresh).length ? { ...fresh, landing_url: window.location.href.slice(0, 500) } : stored;
  try {
    sessionStorage.setItem(TRACKING_STORE, JSON.stringify(tracking));
  } catch {
    // storage may be unavailable (private mode)
  }
  return tracking;
};

// Fires conversion events for whichever ad pixels are installed on the site.
const trackLead = (solution) => {
  try {
    if (window.fbq) window.fbq("track", "Lead", { content_name: solution });
    if (window.gtag) window.gtag("event", "generate_lead", { item_name: solution });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "lead_submitted", solution });
  } catch {
    // never block the user on analytics
  }
};

const BENEFITS = [
  { icon: ShieldCheck, title: "Bank-grade security", text: "256-bit encryption & RBI-compliant partners" },
  { icon: Zap, title: "Fast setup", text: "Most accounts go live within 24–48 hours" },
  { icon: Percent, title: "Best rates", text: "Lowest MDR and transparent pricing" },
  { icon: Headphones, title: "Real people", text: "Dedicated phone & WhatsApp support" },
];

const STEPS = [
  { title: "Fill the form", text: "Takes under a minute — just your name and number." },
  { title: "Get a call back", text: "Our expert calls you within one working day." },
  { title: "Go live", text: "We handle onboarding, KYC and setup for you." },
];

const PARTNERS = ["PhonePe", "Paytm", "Razorpay", "Cashfree", "Subpaisa", "HDFC Bank", "ICICI Bank", "Star Health"];

const ApplyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isThankYou = location.pathname.replace(/\/$/, "").endsWith("/thank-you");

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const offer = OFFERS[(params.get("service") || "").toLowerCase()] || DEFAULT_OFFER;
  const [tracking] = useState(() => readTracking(location.search));
  const [submittedFirstName, setSubmittedFirstName] = useState(location.state?.firstName || "");

  useEffect(() => {
    document.title = isThankYou ? "Thank you | Fi Pay Bills" : `${offer.eyebrow} | Fi Pay Bills`;
  }, [isThankYou, offer]);

  const handleSubmitted = (result) => {
    trackLead(result.solution);
    setSubmittedFirstName(result.fullName.split(" ")[0]);
    // A distinct URL lets Google/Facebook count conversions by page visit.
    navigate(`/apply/thank-you${location.search}`, { replace: false, state: { firstName: result.fullName.split(" ")[0] } });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const whatsappUrl = `https://wa.me/91${OFFICIAL_LINKS.phone}?text=${encodeURIComponent(
    `Hello Fi Pay Bills, I'm interested in ${offer.solution}.`
  )}`;

  return (
    <div className="fi-landing">
      <div className="min-h-screen bg-slate-50 flex flex-col pb-20 lg:pb-0">
        {/* Minimal header: no navigation, so visitors stay focused on the form */}
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <FiPayLogo size="sm" />
            <a
              href={`tel:+91${OFFICIAL_LINKS.phone}`}
              className="hidden sm:inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 text-sm font-bold text-slate-800 hover:bg-slate-50"
            >
              <Phone className="w-4 h-4 text-blue-700" />
              <span>+91 {OFFICIAL_LINKS.phone}</span>
            </a>
          </div>
        </header>

        <main className="flex-1">
          {/* Hero + form */}
          <section className="bg-gradient-to-br from-[#071c45] via-[#0c2e6b] to-[#124294] text-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              <div className="lg:col-span-6 space-y-5 lg:pt-6">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-black uppercase tracking-wider text-emerald-300">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  {offer.eyebrow}
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">{offer.title}</h1>
                <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-xl">{offer.subtitle}</p>

                <ul className="space-y-2.5 hidden sm:block">
                  {offer.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-white">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <div className="hidden lg:flex items-center gap-6 pt-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <span className="font-bold">Rated 5.0 by merchants</span>
                  </div>
                  <div className="flex items-center gap-2 text-blue-100">
                    <Clock className="w-4 h-4" />
                    <span>Call back within 1 working day</span>
                  </div>
                </div>
              </div>

              <div id="apply-form" className="lg:col-span-6">
                <div className="bg-white text-slate-900 rounded-3xl shadow-2xl shadow-blue-950/40 border border-slate-200 p-5 sm:p-7">
                  {isThankYou ? (
                    <ThankYou firstName={submittedFirstName} whatsappUrl={whatsappUrl} />
                  ) : (
                    <>
                      <div className="mb-5">
                        <h2 className="text-xl font-black text-[#0c2e6b]">Get a free consultation</h2>
                        <p className="text-sm text-slate-500 mt-0.5">Share your details and our expert will call you back.</p>
                      </div>
                      <InquiryForm
                        preselectedSolution={offer.solution}
                        source="ad_landing"
                        meta={tracking}
                        onSubmitted={handleSubmitted}
                        autoFocus={false}
                        large
                        showMessage={false}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {BENEFITS.map(({ icon: Icon, title, text }) => (
                <div key={title} className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200">
                  <Icon className="w-5 h-5 text-blue-700 mb-2" />
                  <h3 className="font-bold text-sm text-[#0c2e6b]">{title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* How it works */}
          <section className="bg-white border-y border-slate-200">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
              <h2 className="text-2xl sm:text-3xl font-black text-[#0c2e6b] tracking-tight text-center">How it works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                {STEPS.map((s, i) => (
                  <div key={s.title} className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-blue-700 text-white font-black flex items-center justify-center shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0c2e6b]">{s.title}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{s.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              {!isThankYou && (
                <div className="text-center mt-8">
                  <button
                    onClick={() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className="px-6 py-3 rounded-2xl bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm shadow-md shadow-blue-700/20 cursor-pointer"
                  >
                    Get my free consultation
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Partners */}
          <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-center">
            <p className="text-xs font-semibold text-slate-500 mb-3">Working with India's leading payment, banking & insurance partners</p>
            <div className="flex flex-wrap justify-center gap-2">
              {PARTNERS.map((p) => (
                <span key={p} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-700">
                  {p}
                </span>
              ))}
            </div>
          </section>
        </main>

        <footer className="border-t border-slate-200 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
            <span>&copy; {new Date().getFullYear()} Fi Pay Bills · {OFFICIAL_LINKS.location}</span>
            <span>Insurance and loans are provided by IRDA / RBI-regulated partners.</span>
          </div>
        </footer>

        {/* Mobile quick-contact bar */}
        <div
          className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white border-t border-slate-200 p-3 grid grid-cols-2 gap-2"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <a
            href={`tel:+91${OFFICIAL_LINKS.phone}`}
            className="py-3 rounded-xl border border-blue-200 bg-blue-50 text-blue-800 font-bold text-sm flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" />
            <span>Call us</span>
          </a>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
    </div>
  );
};

const ThankYou = ({ firstName, whatsappUrl }) => (
  <div className="text-center py-6 space-y-4">
    <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
      <CheckCircle className="w-10 h-10" />
    </div>
    <div>
      <h2 className="text-2xl font-black text-slate-900">Thank you{firstName ? `, ${firstName}` : ""}!</h2>
      <p className="text-sm text-slate-600 mt-1">
        We've received your request. Our expert will call you within one working day.
      </p>
    </div>
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex items-center justify-center gap-2"
    >
      <MessageCircle className="w-4 h-4" />
      <span>Need it faster? Chat on WhatsApp</span>
    </a>
    <a href="/" className="block text-sm font-semibold text-blue-700 hover:underline">
      Explore all our services
    </a>
  </div>
);

export default ApplyPage;
